/**
 * GamePage — top-level orchestrator for the active Market Melee game view.
 *
 * This file intentionally owns ONLY:
 *   • Data fetching (gameService) + socket.io lifecycle
 *   • Bot-turn staging (thinking → action banner → apply state)
 *   • Turn-transition overlay state + "I'm Ready" gating
 *   • Action dispatch (doAction) with contextual sound effects
 *
 * All large UI fragments now live in `components/game/board/`:
 *   GameHeader · EventLogPanel · DeckPanel · PhaseActions
 *   PlayerArea · TurnTransitionOverlay · GameOverScreen
 *
 * Pure helpers (extractBotTurns / getRecentAISummary / getStrategyHint)
 * live in `game/gameHelpers.js`.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { WifiOff } from "lucide-react";

import { devError } from "@/lib/logger";
import { gameService } from "@/services";
import { useAuth } from "@/contexts/AuthContext";
import { useSound } from "@/contexts/SoundContext";
import { extractBotTurns } from "@/game/gameHelpers";
import { applyPatch, deepClone } from "fast-json-patch";

import { useCountdown } from "@/hooks/useCountdown";
import { useReactionMute } from "@/hooks/useReactionMute";
import { useTurnNotifications } from "@/hooks/useTurnNotifications";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ChoiceModal from "@/components/game/hud/ChoiceModal";
import EffectCardDetailModal from "@/components/game/hud/EffectCardDetailModal";
import XPSummary from "@/components/game/hud/XPSummary";
import FloatingDelta from "@/components/game/hud/FloatingDelta";
import AIActionIndicator from "@/components/game/hud/AIActionIndicator";
import LiveScoreboard from "@/components/game/board/LiveScoreboard";
import ReactionBar from "@/components/game/social/ReactionBar";
import GameHeader from "@/components/game/board/GameHeader";
import EventLogPanel from "@/components/game/board/EventLogPanel";
import DeckPanel from "@/components/game/board/DeckPanel";
import PhaseActions from "@/components/game/board/PhaseActions";
import PlayerArea from "@/components/game/board/PlayerArea";
import TurnTransitionOverlay from "@/components/game/board/TurnTransitionOverlay";
import GameOverScreen from "@/components/game/board/GameOverScreen";

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

// Bot-turn staging timing constants (ms). Tuned for readability.
const THINK_MIN = 1500; // minimum duration of "thinking…" banner
const BANNER_SINGLE_BOT = 2200; // per-bot action banner for 1-bot turns
const BANNER_MULTI_BOT = 1500; // per-bot action banner for multi-bot
const SETTLE_MS = 5000; // linger after state apply so value change lands under the banner

export default function GamePage() {
  const { t } = useTranslation();
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const isOnline = searchParams.get("online") === "true";
  const isDaily = searchParams.get("daily") === "true";
  const botPlayersParam = searchParams.get("bots");
  const botPlayers = useMemo(
    () => (botPlayersParam ? botPlayersParam.split(",").map(Number) : []),
    [botPlayersParam],
  );
  const navigate = useNavigate();
  const { play: playSound, muted, setMuted } = useSound();
  const reactionMute = useReactionMute(gameId);
  const { user } = useAuth();

  const [game, setGame] = useState(null);
  const [showTurnScreen, setShowTurnScreen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedSellCard, setSelectedSellCard] = useState(null);
  const [inspectingCard, setInspectingCard] = useState(null);
  const [showXPSummary, setShowXPSummary] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [showEventLog, setShowEventLog] = useState(false);
  const [showDecks, setShowDecks] = useState(false);
  const [showSurrenderConfirm, setShowSurrenderConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // AI turn staging
  const [aiThinking, setAiThinking] = useState(null);
  const [aiAction, setAiAction] = useState(null);

  // KI-4 closed (KNOWN_ISSUES §1.44) — fires a desktop / mobile
  // Notification when a turn rotates to this user IF they're in
  // another tab AND opted in via the Profile page toggle.
  useTurnNotifications({
    game,
    currentUserId: user?.user_id,
    isOnline,
  });

  const botTurnInFlight = useRef(false);
  // Timer IDs survive across effect re-runs. Cleanup only clears them on
  // unmount so timers scheduled inside the bot-turn effect cannot be wiped
  // by setGame() flipping a useEffect dependency mid-sequence.
  const botTurnTimersRef = useRef([]);
  const logEndRef = useRef(null);
  const prevPlayerRef = useRef(null);
  const socketRef = useRef(null);
  // Stable ref to the latest game so memoised callbacks (resolveChoice,
  // discardCard, etc. — useCallback with [] for stable refs) always
  // read the current state_version when dispatching an action. Without
  // this, after a 409 → refetch → setGame(fresh), the frozen callback
  // closure keeps sending the stale version → infinite 409 loop.
  const gameRef = useRef(game);
  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  const fetchGame = useCallback(async () => {
    try {
      const data = await gameService.getGame(gameId);
      setGame(data);
    } catch (err) {
      devError("Failed to fetch game:", err);
    }
  }, [gameId]);

  const resetTransientUI = useCallback(() => {
    setSelectedSellCard(null);
    setInspectingCard(null);
    setLoading(false);
  }, []);

  // When the turn clock hits 0, refetch — server's auto-resolve
  // (KNOWN_ISSUES §1.18, now enabled for every mode) fast-forwards
  // through the AFK player's whole turn until control rotates. The
  // 500ms buffer absorbs minor client/server clock skew.
  //
  // Wording: "you" for single-device modes; online differentiates.
  useEffect(() => {
    if (game?.paused) return undefined; // pause freezes the clock
    const expiresAt = game?.turn_expires_at;
    if (!expiresAt) return undefined;
    const deadlineMs = Date.parse(expiresAt);
    if (!Number.isFinite(deadlineMs)) return undefined;
    const delay = Math.max(0, deadlineMs - Date.now()) + 500;
    // `timerId`, NOT `t` — shadowing the i18n `t` from useTranslation
    // breaks every t("...") call inside the setTimeout callback.
    const timerId = setTimeout(() => {
      const g = gameRef.current;
      if (!g) return;
      const isOnlineGame = !!g.online;
      let isMyTurn = true;
      if (isOnlineGame) {
        const userIdMap = g.user_id_map || {};
        const myIdx = user?.user_id
          ? Object.keys(userIdMap).find((k) => userIdMap[k] === user.user_id)
          : null;
        isMyTurn = myIdx != null && Number(myIdx) === g.current_player_index;
      }
      if (isMyTurn) {
        toast.warning(t("game.toast.yourTimeUp.title"), {
          description: t("game.toast.yourTimeUp.description"),
        });
      } else {
        const cur = g.players?.[g.current_player_index];
        toast.info(t("game.toast.timeUp.title"), {
          description: t("game.toast.timeUp.description", {
            name: cur?.name || "Player",
          }),
        });
      }
      fetchGame();
    }, delay);
    return () => clearTimeout(timerId);
  }, [game?.turn_expires_at, game?.paused, fetchGame, user?.user_id, t]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  // Socket.io for online games with reconnect handling
  useEffect(() => {
    if (!isOnline || socketRef.current) return;
    const s = io(SOCKET_URL, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    s.on("connect", () => {
      setReconnecting(false);
      s.emit("join_game", { game_id: gameId });
    });
    s.on("disconnect", () => setReconnecting(true));
    s.on("reconnect", () => {
      setReconnecting(false);
      s.emit("join_game", { game_id: gameId });
      // Server may have advanced state while we were offline (turn clock
      // expired, opponent surrendered, our pending action got auto-resolved).
      // Snap transient UI back to nothing so the next setGame() drives a
      // clean re-render off the authoritative server state.
      resetTransientUI();
      fetchGame();
    });
    s.on("reconnect_attempt", () => setReconnecting(true));
    s.on("game_update", (data) => setGame(data));
    // KNOWN_ISSUES §1.42 — server-side delta optimisation. When the
    // backend kill switch is on AND we have a current local state
    // matching `from_version`, apply the JSON Patch in place. On any
    // mismatch (missed frame, stale local state) refetch the full
    // game via HTTP so we resync without dropping the socket.
    s.on("game_update_delta", (data) => {
      setGame((prev) => {
        if (!prev) {
          // No local baseline — server should have sent a full first;
          // refetch to recover.
          fetchGame();
          return prev;
        }
        if (prev.state_version !== data.from_version) {
          fetchGame();
          return prev;
        }
        try {
          const next = applyPatch(
            deepClone(prev),
            data.patch,
            false,
            false,
          ).newDocument;
          return next;
        } catch (err) {
          // jsonpatch threw — couldn't apply cleanly. Refetch.
          fetchGame();
          return prev;
        }
      });
    });
    socketRef.current = s;
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isOnline, gameId, fetchGame, resetTransientUI]);

  // Auto-play bot turns with a deliberate, readable staging:
  //   thinking (≥1000ms, stable) →
  //   action banner + scoreboard highlight (≥900ms single / 550ms multi) →
  //   apply state while highlight lingers (500ms "settle") →
  //   clear banner, release guard → human transition (~500ms)
  //
  // Budget: 1-bot vs-AI ≈ 2.4s; 3-bot daily ≈ 3.2s (within 2–3.5s target).
  useEffect(() => {
    if (!game || game.status !== "active") return;
    const currentBots = game.bot_players || botPlayers;
    if (currentBots.length === 0) return;
    if (!currentBots.includes(game.current_player_index)) {
      if (aiThinking !== null) setAiThinking(null);
      if (aiAction !== null) setAiAction(null);
      botTurnInFlight.current = false;
      return;
    }
    if (botTurnInFlight.current) return;

    botTurnInFlight.current = true;
    const botIdx = game.current_player_index;
    const botNames = currentBots
      .map((i) => game.players[i]?.name)
      .filter(Boolean);
    const startingBotName = game.players[botIdx]?.name || "Bot";
    const prevLogLen = (game.event_log || []).length;

    setAiThinking(startingBotName);

    const push = (id) => botTurnTimersRef.current.push(id);
    const startTs = performance.now();

    (async () => {
      try {
        const data = await gameService.runBotTurn(gameId);
        const newEntries = (data.event_log || []).slice(prevLogLen);
        const turns = extractBotTurns(newEntries, botNames);

        // Stall backstop: the backend now always rotates control off a bot
        // it can't act for, so a returned state still parked on a bot is
        // abnormal. Because the effect deps won't change (same
        // current_player_index), it won't re-fire on its own — so clear the
        // "thinking" indicator, release the guard, and refetch once to
        // resync rather than leaving the turn visually hung.
        //
        // EXCEPTION: a bot turn that ends with a pending choice aimed at a
        // HUMAN is NOT stuck — the bot cast a card the human must respond to,
        // and the ChoiceModal will render for them. Treating that as a stall
        // would flash a bogus "bot stuck" toast every time a bot plays a
        // human-targeting card.
        const botList = data.bot_players || currentBots;
        const stillBotTurn = botList.includes(data.current_player_index);
        const pa = data.pending_action;
        const pendingForHuman = pa && !botList.includes(pa.for_player);
        if (stillBotTurn && !pendingForHuman) {
          setAiThinking(null);
          setAiAction(null);
          botTurnInFlight.current = false;
          toast.info(t("game.toast.botStuck.title"), {
            description: t("game.toast.botStuck.description"),
          });
          push(setTimeout(() => fetchGame(), 800));
          return;
        }

        const elapsed = performance.now() - startTs;
        const thinkDelay = Math.max(0, THINK_MIN - elapsed);

        push(
          setTimeout(() => {
            if (turns.length === 0) {
              setAiThinking(null);
              // Rare: no decision step (e.g. bot forced-skipped). Still
              // pause briefly so state doesn't snap instantly.
              push(
                setTimeout(() => {
                  botTurnInFlight.current = false;
                  setGame(data);
                }, 300),
              );
              return;
            }
            const perBanner =
              turns.length === 1 ? BANNER_SINGLE_BOT : BANNER_MULTI_BOT;
            // Atomic hand-off: clear thinking AND show the first action in
            // the same React batch so the banner transitions smoothly.
            setAiThinking(null);
            setAiAction(turns[0]);
            let cumulative = perBanner;
            for (let i = 1; i < turns.length; i++) {
              const t = turns[i];
              push(setTimeout(() => setAiAction(t), cumulative));
              cumulative += perBanner;
            }
            // Apply state AFTER banners have been on screen long enough.
            // The banner + row highlight stay visible for SETTLE_MS after
            // the value change lands, so cause (banner) + effect (number
            // change) land in one continuous moment.
            push(setTimeout(() => setGame(data), cumulative));
            push(
              setTimeout(() => {
                setAiAction(null);
                botTurnInFlight.current = false;
              }, cumulative + SETTLE_MS),
            );
          }, thinkDelay),
        );
      } catch (err) {
        setAiThinking(null);
        setAiAction(null);
        botTurnInFlight.current = false;
        devError("Bot turn failed:", err);
      }
    })();
    // NB: intentionally no per-run cleanup. Timers live in a ref and
    // are cleared only on UNMOUNT (below) — clearing on re-run strands
    // the banner and in-flight guard (prior regression).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    game?.current_player_index,
    game?.turn_phase,
    game?.status,
    gameId,
    botPlayers,
    game?.bot_players,
  ]);

  // Unmount-only cleanup for bot-turn timers.
  useEffect(() => {
    return () => {
      botTurnTimersRef.current.forEach((id) => clearTimeout(id));
      botTurnTimersRef.current = [];
    };
  }, []);

  // Show XP summary when game ends
  useEffect(() => {
    if (
      game?.status === "finished" &&
      game?.xp_results &&
      Object.keys(game.xp_results).length > 0
    ) {
      setShowXPSummary(true);
      playSound("winFanfare");
    }
  }, [game?.status, game?.xp_results, playSound]);

  useEffect(() => {
    if (logEndRef.current)
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [game?.event_log?.length]);

  useEffect(() => {
    const currentPlayerIdx = game?.current_player_index;
    const gameBots = game?.bot_players;
    // Use `currentPlayerIdx != null` (not `if (game)`) so the body
    // doesn't reference the `game` object directly — keeps the dep
    // array narrow (only the slices we actually read) without
    // re-firing on every state tick. `current_player_index` is always
    // present on a real game state, so it's a valid presence proxy.
    if (
      currentPlayerIdx != null &&
      prevPlayerRef.current !== null &&
      prevPlayerRef.current !== currentPlayerIdx
    ) {
      // In vs-AI / Daily (bot_players non-empty): skip the "Ready"
      // overlay for BOT turns entirely — the AI staging banners ARE
      // the indicator. For the HUMAN the overlay is a hard manual
      // handoff: it stays on screen and blocks further progression
      // until the player clicks "I'm Ready". Pass-and-play semantics.
      // Set explicitly both ways so a bot's turn_start can never
      // inherit a stale `true` from an earlier render — that's how
      // the bot's I'm-Ready screen used to flash.
      const currentBots = gameBots || botPlayers;
      const nextIsBot = currentBots.includes(currentPlayerIdx);
      setShowTurnScreen(!nextIsBot);
      playSound("swoosh");
    }
    if (currentPlayerIdx != null) prevPlayerRef.current = currentPlayerIdx;
  }, [game?.current_player_index, game?.bot_players, playSound, botPlayers]);

  const doAction = async (action) => {
    if (loading) return;
    if (gameRef.current?.paused) {
      toast.info(t("game.toast.paused.title"), {
        description: t("game.toast.paused.description"),
      });
      return;
    }
    setLoading(true);
    const soundMap = {
      buy: "coinClick",
      sell: "cardSlide",
      hold: "holdThud",
      draw_effect: "cardDraw",
      play_card: "playCard",
      confirm_turn: "swoosh",
      discard: "cardSlide",
      continue: "click",
    };
    if (soundMap[action.action]) playSound(soundMap[action.action]);
    try {
      // V-5/V-6: every action carries the version we last saw so the backend
      // can 409 a duplicate retry / concurrent race instead of double-applying.
      // Read from the ref (NOT closure) so memoised callers like
      // resolveChoice always send the latest version — closures would
      // freeze on the version that was current when the callback was
      // created, looping 409 → refetch → 409 forever.
      const expectedVersion = gameRef.current?.state_version ?? 0;
      const data = await gameService.sendAction(
        gameId,
        action,
        expectedVersion,
      );
      // Backend flags this when XP couldn't be saved despite the game
      // finishing (transient Mongo error, etc). Game still ends correctly,
      // the user's XP just didn't tick — surface so they don't think the
      // game silently swallowed their reward. (Toast lib not wired up yet;
      // for now a console warn + fail buzz is the cue.)
      if (data?.xp_award_failed) {
        // eslint-disable-next-line no-console
        console.warn(
          "XP award failed for finished game — please contact support if your level didn't update.",
        );
        playSound("failBuzz");
      }
      setGame((prev) => {
        const next = data;
        if (next.status === "finished") playSound("winFanfare");
        else if (next.market_melee_count > (prev?.market_melee_count || 0))
          playSound("meleeImpact");
        else if (next.event_log?.length > (prev?.event_log?.length || 0)) {
          const last = next.event_log[next.event_log.length - 1];
          if (last?.type === "event") playSound("eventBell");
          if (last?.type === "buy_fail") playSound("failBuzz");
        }
        return next;
      });
      setSelectedSellCard(null);
    } catch (err) {
      // V-5/V-6 conflict path: backend says our state is stale (duplicate
      // retry, concurrent action, lost CAS race). Also covers a stale phase
      // rejection (e.g. `continue`/`resolve_choice` against an advanced
      // server). In every case the action wasn't applied, so refetch and let
      // the UI reconcile instead of leaving the player wedged.
      const status = err?.response?.status;
      if (status) {
        try {
          const fresh = await gameService.getGame(gameId);
          setGame(fresh);
        } catch {
          /* refetch failed — leave UI alone, next reconnect will recover */
        }
      }
      playSound("failBuzz");
    }
    setLoading(false);
  };

  const xpStats = useMemo(
    () => ({ melee_count: game?.market_melee_count || 0 }),
    [game?.market_melee_count],
  );

  // If the server state advances past a card we're inspecting (hand
  // changed, turn moved, pending action resolved), close the modal so
  // the player can't try to play a card that's no longer there. Only
  // checks the current player's hand — inspecting an opponent card is
  // not a path the UI exposes.
  useEffect(() => {
    if (!inspectingCard || !game) return;
    const me = game.players?.[game.current_player_index];
    const stillInHand = me?.hand?.some((c) => c?.id === inspectingCard.id);
    if (!stillInHand) setInspectingCard(null);
  }, [inspectingCard, game]);

  // Close every transient overlay when the current player rotates.
  // After a timeout auto-skip, the previous player's open modals
  // (inspect, sell selection, surrender prompt) shouldn't carry over
  // to the next player's view. The choice modal is keyed on
  // state_version so it remounts naturally; this covers the rest.
  useEffect(() => {
    setInspectingCard(null);
    setSelectedSellCard(null);
    setShowSurrenderConfirm(false);
  }, [game?.current_player_index]);

  // Phase 2 surrender — opens a styled AlertDialog (was native
  // window.confirm — felt like a bug in 2026 next to the rest of the
  // shadcn-styled UI). Available in vs-AI / Daily / online; hidden in
  // pass-and-play, where surrender is pointless since the same device
  // controls every player.
  const isPassAndPlay = game && !(game.online || game.vs_ai || game.daily);
  // Stable handler refs — memo'd children below skip re-renders when
  // props haven't shallow-changed; inline arrow callbacks would defeat
  // that on every parent render. (KNOWN_ISSUES §1.36)
  const openSurrenderConfirm = useCallback(() => {
    if (loading || game?.status !== "active") return;
    setShowSurrenderConfirm(true);
  }, [loading, game?.status]);
  const confirmSurrender = useCallback(() => {
    setShowSurrenderConfirm(false);
    doAction({ action: "surrender" });
    // Don't navigate — the game state drives the UI from here.
    // 1v1 surrender ends the game via the all-but-one-forfeited
    // rule → GameOverScreen + XP summary render naturally with the
    // breakdown the user just earned. Multi-player surrenders leave
    // them as a forfeited spectator; the engine fixes (clear pending
    // on surrender, skip forfeited slots in multi-player chains)
    // ensure they aren't pinged for choices on their forfeited slot.
    // doAction reads `loading` etc. from closure; intentionally omitted
    // from deps to keep the surrender ref stable across non-meaningful
    // re-renders. Same pattern as the pass-and-play handlers below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeXPSummary = useCallback(() => setShowXPSummary(false), []);
  const showXP = useCallback(() => setShowXPSummary(true), []);
  const backToLobby = useCallback(() => navigate("/dashboard"), [navigate]);
  // Guard against accidental back-taps mid-game. The game state is
  // server-persisted (the user can resume from the lobby), but a
  // surprise nav still feels destructive — surface a confirm so the
  // tap is always intentional.
  const requestLeave = useCallback(() => {
    if (gameRef.current?.status === "finished") {
      backToLobby();
      return;
    }
    setShowLeaveConfirm(true);
  }, [backToLobby]);
  const confirmLeave = useCallback(() => {
    setShowLeaveConfirm(false);
    backToLobby();
  }, [backToLobby]);

  // Intercept the browser/device back button (hardware back on
  // Android, swipe-back gesture on iOS, browser back arrow). We push
  // an extra history entry on mount; when the user backs out, popstate
  // fires, we re-push to keep the URL anchored, and surface the
  // leave-confirm dialog. The user explicitly clicking "Leave"
  // navigates via React Router which doesn't trip this listener.
  useEffect(() => {
    if (!gameId) return undefined;
    window.history.pushState({ mmGameGuard: true }, "");
    const onPopState = () => {
      const g = gameRef.current;
      if (!g || g.status === "finished") {
        // Game's over — let the back take effect normally.
        return;
      }
      // Re-anchor history so we stay on this URL while the dialog is open.
      window.history.pushState({ mmGameGuard: true }, "");
      setShowLeaveConfirm(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [gameId]);

  // Pass-and-play pause/resume. Backend toggles `state.paused` and
  // nulls out `turn_expires_at` so the timer stops; resume hands the
  // current player a fresh full-phase clock.
  const togglePause = useCallback(async () => {
    const g = gameRef.current;
    if (!g || g.status !== "active") return;
    try {
      const data = g.paused
        ? await gameService.resumeGame(gameId)
        : await gameService.pauseGame(gameId);
      setGame(data);
    } catch (err) {
      devError("Pause/resume failed:", err);
    }
  }, [gameId]);
  const closeEventLog = useCallback(() => setShowEventLog(false), []);
  const inspectCard = useCallback((c) => setInspectingCard(c), []);
  const cancelInspect = useCallback(() => setInspectingCard(null), []);
  const discardCard = useCallback(
    (cardId) => doAction({ action: "discard", card_id: cardId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const playInspectedCard = useCallback(() => {
    const id = inspectingCard?.id;
    setInspectingCard(null);
    if (id) doAction({ action: "play_card", card_id: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectingCard]);
  const resolveChoice = useCallback(
    (choice) => doAction({ action: "resolve_choice", choice }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Phase 2 opponent-disconnected banner — surface when ANY other player
  // has a `disconnect_grace_until` set (regardless of whether the timer
  // has expired; the lazy resolver flips them to forfeited on the next
  // request, at which point this banner naturally clears).
  const disconnectedOpponents = (game?.players || [])
    .map((p, idx) => ({ ...p, idx }))
    .filter(
      (p) =>
        !p.forfeited &&
        p.disconnect_grace_until &&
        // Don't show "you disconnected" to yourself.
        !(game?.bot_players || []).includes(p.idx),
    );

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E3F2FD] dark:bg-[#0a1428]">
        <div
          className="text-[#1A237E] dark:text-[#E3F2FD] text-xl font-bold animate-pulse"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("game.loading")}
        </div>
      </div>
    );
  }

  const currentPlayer = game.players[game.current_player_index];
  const phase = game.turn_phase;
  const isFinished = game.status === "finished";

  // ── PAUSED OVERLAY (pass-and-play) ──
  // Sits above every other overlay so a paused game is unmistakable
  // and uninterruptible. Resume button restores the clock.
  if (game.paused) {
    return (
      <div
        className="min-h-screen bg-[#1A237E] flex items-center justify-center p-4"
        data-testid="paused-overlay"
      >
        <div className="max-w-md px-4 text-center text-white">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-5 rounded-full bg-white/15">
            <span className="text-5xl" aria-hidden="true">
              ⏸️
            </span>
          </div>
          <h2
            className="mb-2 text-4xl font-bold sm:text-5xl"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("game.paused.title")}
          </h2>
          <p className="mb-6 text-base opacity-80">
            {t("game.paused.subtitle")}
          </p>
          <Button
            data-testid="resume-btn"
            onClick={togglePause}
            className="rounded-full px-10 py-4 text-lg font-bold bg-[#38B6FF] hover:bg-[#2196F3] text-white shadow-[0_4px_0_rgba(0,0,0,0.3)] hover:shadow-[0_2px_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] transition-all active:scale-95"
          >
            <span className="mr-2 text-2xl" aria-hidden="true">
              ▶
            </span>
            {t("game.button.resume")}
          </Button>
        </div>
      </div>
    );
  }

  // ── TURN TRANSITION OVERLAY ──
  // The overlay stays up until the server response flips the phase
  // OUT of `turn_start` — that's also the moment the turn clock
  // starts ticking server-side (set_turn_expiry skips turn_start, so
  // the deadline is null until confirm_turn lands and we drop to
  // `decision`). No fake "you have 30s to click ready" pressure.
  //
  // Hard guard against ever showing the overlay for a bot's
  // turn_start. The rotation effect flips `showTurnScreen` to false
  // when control passes to a bot, but effects run AFTER render — so
  // for one frame after the user's turn ends and the new state
  // (current=BOT, phase=turn_start) lands, the stale `showTurnScreen=
  // true` from the user's own ready-click would render the bot's
  // ready screen. Checking `isCurrentBot` at render time closes that
  // gap completely.
  const currentIsBot = (game.bot_players || botPlayers || []).includes(
    game.current_player_index,
  );
  if (
    showTurnScreen &&
    !isFinished &&
    phase === "turn_start" &&
    !currentIsBot
  ) {
    return (
      <TurnTransitionOverlay
        game={game}
        currentPlayer={currentPlayer}
        loading={loading}
        onReady={() => {
          if (loading) return;
          playSound("shuffle");
          doAction({ action: "confirm_turn" });
        }}
      />
    );
  }

  // ── GAME OVER SCREEN ──
  if (isFinished) {
    if (showXPSummary && game.xp_results) {
      return (
        <XPSummary
          xpResults={game.xp_results}
          players={game.players}
          winner={game.winner}
          onClose={closeXPSummary}
          stats={xpStats}
        />
      );
    }
    return (
      <GameOverScreen
        game={game}
        onShowXP={showXP}
        onBackToLobby={backToLobby}
      />
    );
  }

  return (
    <TooltipProvider>
      <div className="game-board" data-testid="game-board">
        {/* ── RECONNECTING OVERLAY ── */}
        {reconnecting && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1A237E]/80 dark:bg-[#E3F2FD]/80 backdrop-blur-sm"
            data-testid="reconnecting-overlay"
          >
            <div className="text-center text-white">
              <WifiOff className="w-12 h-12 mx-auto mb-3 animate-pulse" />
              <p
                className="mb-2 text-lg font-bold"
                style={{ fontFamily: "Fredoka" }}
              >
                {t("game.reconnecting.title")}
              </p>
              <p className="text-sm opacity-70">
                {t("game.reconnecting.subtitle")}
              </p>
              <Button
                variant="outline"
                className="mt-4 text-white rounded-full border-white/30 hover:bg-white/10 dark:bg-slate-900/10"
                onClick={() => {
                  fetchGame();
                  setReconnecting(false);
                }}
              >
                {t("game.button.retryNow")}
              </Button>
            </div>
          </div>
        )}

        <GameHeader
          game={game}
          phase={phase}
          currentPlayerName={currentPlayer.name}
          isDaily={isDaily}
          muted={muted}
          setMuted={setMuted}
          showEventLog={showEventLog}
          setShowEventLog={setShowEventLog}
          showDecks={showDecks}
          setShowDecks={setShowDecks}
          onBack={requestLeave}
          // Surrender available in networked / vs-AI / Daily modes only.
          onSurrender={
            isPassAndPlay || isFinished ? undefined : openSurrenderConfirm
          }
          // Pause is pass-and-play only — backend 403s for other modes.
          canPause={isPassAndPlay && !isFinished}
          onTogglePause={togglePause}
        />

        {/* Phase 2 + 3a — opponent-disconnected indicator with live
            countdown. Yellow banner under the header listing each
            disconnected player and the seconds remaining on their
            grace timer. The `useCountdown` hook ticks 1Hz and stops
            on its own when the deadline passes, so when grace expires
            and the next request flips them to forfeited, this banner
            naturally clears (forfeited players are filtered out). */}
        {disconnectedOpponents.length > 0 && !isFinished && (
          <div
            className="flex flex-wrap px-4 py-2 text-xs font-semibold border-b bg-amber-100 border-amber-300 text-amber-900 gap-x-4 gap-y-1"
            data-testid="disconnect-banner"
          >
            {disconnectedOpponents.map((p) => (
              <DisconnectChip key={p.idx} player={p} />
            ))}
          </div>
        )}

        {/* ── MAIN AREA ──
            SCROLL FIX: no overflow-hidden so the page body is the single
            primary scroll container. Children that need their own scroll
            (event log) use explicit max-height. */}
        <div className="relative flex flex-1">
          <EventLogPanel
            game={game}
            show={showEventLog}
            onClose={closeEventLog}
            logEndRef={logEndRef}
          />

          {/* CENTER: Play Area
              SCROLL FIX: justify-start keeps controls at the top so
              Buy/Sell/Hold are always reachable from a normal page scroll. */}
          <main
            className="relative flex flex-col items-center justify-start flex-1 gap-3 p-3 sm:p-6 sm:gap-6"
            data-testid="play-area"
          >
            <AIActionIndicator thinking={aiThinking} action={aiAction} />
            <div className="relative flex justify-center w-full">
              <FloatingDelta
                value={currentPlayer.crypto_value}
                label="crypto"
                resetKey={game.current_player_index}
              />
            </div>

            <PhaseActions
              game={game}
              phase={phase}
              currentPlayer={currentPlayer}
              loading={loading}
              selectedSellCard={selectedSellCard}
              doAction={doAction}
              playSound={playSound}
              hasPendingChoice={!!game.pending_action}
            />

            <LiveScoreboard
              game={game}
              aiAction={aiAction}
              mute={reactionMute}
            />
          </main>

          <DeckPanel
            game={game}
            currentPlayer={currentPlayer}
            show={showDecks}
            onClose={() => setShowDecks(false)}
          />
        </div>

        <PlayerArea
          currentPlayer={currentPlayer}
          phase={phase}
          selectedSellCard={selectedSellCard}
          setSelectedSellCard={setSelectedSellCard}
          onInspectCard={inspectCard}
          onDiscardCard={discardCard}
        />

        {/* ── HAND CARD INSPECT/PLAY CONFIRMATION ── */}
        <EffectCardDetailModal
          card={inspectingCard}
          loading={loading}
          onCancel={cancelInspect}
          onPlay={playInspectedCard}
        />

        {/* ── CHOICE MODAL ──
            Decision-ownership guard: only render the choice modal if the
            pending action is for the HUMAN. When a human's action forces a
            bot to choose (e.g. Insider Trading on a bot), the backend now
            auto-resolves it via AIBot.resolve_pending before returning state,
            so this condition should be false. Kept as a defense-in-depth
            safety net so the human can NEVER pick cards on behalf of the AI.
            `options` guard handles online V-1 redaction: non-target viewers
            receive a pending_action with `options` stripped (per-recipient
            broadcast in `socket_handler.emit_per_recipient`); only the actual
            target gets the full payload, so the modal renders only for them. */}
        {game.pending_action &&
          game.pending_action.options &&
          !(game.bot_players || []).includes(
            game.pending_action.for_player,
          ) && (
            <ChoiceModal
              key={`pa-${game.state_version ?? 0}-${game.pending_action.type}-${game.pending_action.for_player}`}
              pendingAction={game.pending_action}
              players={game.players}
              currentPlayerIndex={game.current_player_index}
              onResolve={resolveChoice}
              loading={loading}
              onSurrender={isPassAndPlay ? undefined : openSurrenderConfirm}
            />
          )}

        {/* ── IN-GAME REACTIONS ── */}
        {!isFinished && (
          <ReactionBar
            gameId={gameId}
            playerIdx={game.current_player_index || 0}
            socket={socketRef.current}
            isMuted={reactionMute.isMuted}
          />
        )}

        <AlertDialog
          open={showSurrenderConfirm}
          onOpenChange={setShowSurrenderConfirm}
        >
          <AlertDialogContent data-testid="surrender-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("game.surrenderDialog.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("game.surrenderDialog.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="surrender-cancel-btn">
                {t("game.button.stayIn")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmSurrender}
                className="text-white bg-red-600 hover:bg-red-700"
                data-testid="surrender-confirm-btn"
              >
                {t("game.button.surrender")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
          <AlertDialogContent data-testid="leave-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("game.leaveDialog.title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("game.leaveDialog.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="leave-cancel-btn">
                {t("game.button.stayIn")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmLeave}
                data-testid="leave-confirm-btn"
              >
                {t("game.leaveDialog.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}

/**
 * Phase 3a — opponent-disconnected chip with live countdown.
 *
 * Renders one row of the disconnect banner. The 1Hz tick comes from
 * `useCountdown(player.disconnect_grace_until)`; once the deadline
 * passes, the backend's lazy resolver flips `forfeited: true` on the
 * next request and the parent filters the player out of
 * `disconnectedOpponents`, so this component unmounts naturally.
 */
function DisconnectChip({ player }) {
  const { t } = useTranslation();
  const seconds = useCountdown(player.disconnect_grace_until);
  // Visual urgency — reuse the banner's amber tone but bump to red as
  // the deadline closes in, signalling "they're about to be forfeited".
  const tone =
    seconds <= 10
      ? "text-red-700 font-bold"
      : seconds <= 30
        ? "text-amber-900 font-semibold"
        : "text-amber-800";
  return (
    <span data-testid={`disconnect-banner-${player.idx}`} className={tone}>
      ⚠ {t("game.banner.disconnected", { name: player.name, seconds })}
    </span>
  );
}
